import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useTheme } from '../components/ThemeProvider';
import CiScoreCard from '../components/commerce/CiScoreCard';
import CategoryHeatmap from '../components/commerce/CategoryHeatmap';
import BreakoutSkuList from '../components/commerce/BreakoutSkuList';
import { AlertCircle, ArrowLeft, Moon, Sun } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CommerceDashboardPage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [ciData, setCiData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ciRes, catRes, prodRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/commerce/ci/today`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/commerce/categories`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/commerce/products/trending`, { withCredentials: true })
      ]);

      setCiData(ciRes.data);
      setCategories(catRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error('[Commerce] Error fetching data:', error);
      toast.error('Failed to load commerce data');
    } finally {
      setLoading(false);
    }
  };

  const mockAlerts = [
    {
      id: 'alert_001',
      type: 'breakout',
      title: 'BREAKOUT',
      description: 'Baby Bottle Sterilizer up +72% in 48h',
      category: 'Baby & Kids'
    },
    {
      id: 'alert_002',
      type: 'price_shock',
      title: 'PRICE SHOCK',
      description: 'Fitness Bands +19% overnight',
      category: 'Fitness'
    },
    {
      id: 'alert_003',
      type: 'creator_surge',
      title: 'CREATOR SURGE',
      description: '@skincare_science driving spike in Retinol Serum',
      category: 'Beauty'
    },
    {
      id: 'alert_004',
      type: 'breakout',
      title: 'TRENDING',
      description: 'Smart Pet Feeders +68% this week',
      category: 'Pet Supplies'
    }
  ];

  const getAlertColor = (type) => {
    switch (type) {
      case 'breakout': return '#2EE6A6';
      case 'price_shock': return '#FF4D5A';
      case 'creator_surge': return '#2176FF';
      case 'supply_issue': return '#FFA726';
      default: return '#8A90A2';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        background: 'hsl(var(--background))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'hsl(var(--foreground))'
      }}>
        <div className="text-center">
          <div className="animate-pulse mb-2">Loading Commerce Index...</div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]">Analyzing market signals</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'hsl(var(--background))',
      color: 'hsl(var(--foreground))',
      padding: '2rem'
    }}>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/selector')}
              className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Platform Selector
            </Button>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            Commerce Index
            <Badge variant="secondary" className="ml-3 text-xs">Preview Mode</Badge>
          </h1>
          <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
            The real-time pulse of what the world is buying
          </p>
          <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem', fontStyle: 'italic' }}>
            Demo data for illustration • Full launch Q1 2025
          </p>
        </div>
        
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Left Column - CI Score (spans 2 columns) */}
        <div className="lg:col-span-2">
          {ciData && (
            <CiScoreCard
              value={ciData.value}
              delta={ciData.deltaSinceYesterday}
              history7d={ciData.history7d}
              subIndexes={ciData.subIndexes}
            />
          )}
        </div>

        {/* Right Column - Alerts */}
        <Card style={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }}>
          <CardHeader>
            <CardTitle className="text-base" style={{ 
              fontSize: '0.75rem', 
              fontWeight: 600,
              color: '#8A90A2',
              letterSpacing: '0.05em'
            }}>
              LIVE ALERTS
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {mockAlerts.map(alert => (
              <div
                key={alert.id}
                style={{
                  padding: '0.75rem',
                  background: 'hsl(var(--muted))',
                  border: `1px solid ${getAlertColor(alert.type)}`,
                  borderRadius: '0.25rem',
                  borderLeftWidth: 4
                }}
              >
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={12} style={{ color: getAlertColor(alert.type) }} />
                  <span style={{ 
                    fontSize: '0.6875rem', 
                    fontWeight: 700,
                    color: getAlertColor(alert.type),
                    letterSpacing: 0.5
                  }}>
                    {alert.title}
                  </span>
                </div>
                <div style={{ fontSize: '0.8125rem', color: '#F5F7FF', lineHeight: 1.4 }}>
                  {alert.description}
                </div>
                {alert.category && (
                  <div style={{ fontSize: '0.6875rem', color: '#8A90A2', marginTop: 4 }}>
                    {alert.category}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Category Heatmap */}
      <div className="mb-6">
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#F5F7FF' }}>
          Category Pulse
        </h2>
        <CategoryHeatmap 
          categories={categories} 
          onCategoryClick={(id) => navigate(`/commerce/category/${id}`)}
        />
      </div>

      {/* Breakout SKUs */}
      <div>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '1rem', color: '#F5F7FF' }}>
          Trending Now
        </h2>
        <BreakoutSkuList products={products.slice(0, 10)} />
      </div>
    </div>
  );
}
